import { NavLink } from "react-router-dom";
import { LayoutDashboard, Users, CalendarDays, ShoppingCart, Menu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { roleBlocksPath } from "@/lib/nav";
import { cn } from "@/lib/utils";

// Thumb-reachable mobile primary nav (lg:hidden). Secondary destinations live in the
// existing drawer, opened via "More". Respects RBAC + feature flags and iOS safe-area.
export function BottomNav({ onMore }) {
  const { user } = useAuth();
  const flags = user?.tenant?.feature_flags || {};
  const sflags = user?.server_flags || {};
  const can = (flag, perm, serverFlag) =>
    (!flag || flags[flag]) && (!perm || hasPerm(user, perm)) && (!serverFlag || sflags[serverFlag]);

  const commandTo = can("command_center", "reports.view") ? "/command-center" : "/dashboard";
  const items = [
    { to: commandTo, label: "Command", icon: LayoutDashboard, testid: "bottom-nav-command-center", show: true },
    { to: "/customers", label: "Customers", icon: Users, testid: "bottom-nav-customers", show: can("customers") },
    { to: "/bookings", label: "Bookings", icon: CalendarDays, testid: "bottom-nav-bookings", show: can("bookings") },
    { to: "/pos", label: "POS", icon: ShoppingCart, testid: "bottom-nav-pos", show: can("pos") },
  ].filter((i) => i.show && !roleBlocksPath(user, i.to));

  const cls = ({ isActive }) =>
    cn(
      "flex flex-1 min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 min-h-[44px] text-[11px] font-semibold transition-colors",
      isActive ? "bg-pawkt-primary-soft text-pawkt-primary-deep" : "text-pawkt-text-secondary active:bg-pawkt-elevated"
    );

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-pawkt-border bg-white/95 backdrop-blur-md"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="bottom-nav"
    >
      <div className="flex items-stretch justify-around gap-0.5 px-1.5 pt-1.5 pb-1.5">
        {items.map((it) => (
          <NavLink key={it.to + it.label} to={it.to} data-testid={it.testid} className={cls} end={it.to === "/dashboard"}>
            <it.icon className="h-5 w-5 shrink-0" />
            <span className="truncate max-w-full">{it.label}</span>
          </NavLink>
        ))}
        <button
          onClick={onMore}
          data-testid="bottom-nav-more"
          className="flex flex-1 min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl py-1.5 min-h-[44px] text-[11px] font-semibold text-pawkt-text-secondary active:bg-pawkt-elevated"
        >
          <Menu className="h-5 w-5 shrink-0" />
          <span className="truncate max-w-full">More</span>
        </button>
      </div>
    </nav>
  );
}
